/**
 * Route tree (code-based TanStack Router).
 *
 *   /signin /setup /trust /i/$code              public / onboarding (invite links → /setup)
 *   /enroll                                     add the machine whose `vikod` opened this page
 *
 * The tab shell (default; navigation-ia.md §3.2, lib/ia.ts):
 *   /                                            → the last project tab (Home without projects)
 *   /home[?v=needs|running|projects&project=&i=] Home (tab 0)
 *   /p/$id[/history|/releases|/artifacts|/settings]  a project tab and its views (`?i=` inspector)
 *   /p/$id/s/$runId[?seq=]                       a run's full conversation in its project's chrome
 *   /settings[/$section]  /settings/enroll       Settings (You + Rules + Research library)
 *   /share  /voice  /capture                     share target, hands-free voice, legacy share path
 *   /tree                                        phone: Projects and lanes (the Explorer as a page)
 *
 * Old URLs (/now, /inbox, /overview, /projects, /w, /t, /s, /you, /rules, /releases) redirect
 * (routes/legacy.tsx); with `?flags=-lanesTab` they render the old pages for one more release.
 */
import { type QueryClient, useQuery } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router'
import { AppShell } from './components/AppShell.js'
import { VMark } from './components/brand.js'
import { OverviewPage } from './components/overview/Overview.js' // g12-overview
import { ExplorerPage } from './components/shell/ExplorerPage.js'
import { type Backend, useBackend } from './lib/backend.js'
import { endorsed, endorsementState, loadDevice } from './lib/device-key.js'
import { isEnabled } from './lib/flags.js' // lanes-shell
import { readPending } from './lib/links.js'
import { qk, sessionQuery, useSession } from './lib/queries.js'
import { LanesGallery } from './mock/LanesGallery.js' // lanes-shell (mock-only route)
import { Setup, SignIn, TrustDevice } from './routes/auth.js'
import { CapturePage } from './routes/capture.js'
import { EnrollLinkPage, EnrollPage } from './routes/enroll.js'
// ---- ia-shell ----
import { HomePage, validateHomeSearch } from './routes/home.js'
import { InboxPage } from './routes/inbox.js'
import { LegacyRedirect, legacyPage } from './routes/legacy.js'
import { NowPage } from './routes/now.js'
import {
  GoalTab,
  LanesTab,
  ProjectRunPage,
  ProjectTabIndex,
  ProjectTabPage,
} from './routes/project-tab.js' // lanes-shell
import {
  ProjectArtifactsView,
  ProjectBuildsView,
  ProjectHistoryView,
  ProjectSettingsView,
} from './routes/project-views.js'
import { ProjectsPage } from './routes/projects.js'
import { ReleasesPage } from './routes/releases.js'
import { RulesPage } from './routes/rules.js'
import {
  AppearanceKeysSection,
  RulesSection,
  SettingsIndex,
  SettingsLayout,
} from './routes/settings.js'
import { SharePage } from './routes/share.js' // share-to-viko
import { ResearchSection, VoicePage } from './routes/voice.js' // g8-voice
import { TaskPage, WorkstreamPage } from './routes/workstream.js'
import { AccountSection } from './routes/you/account.js'
import { AdvancedSection } from './routes/you/advanced.js'
import { AppearanceSection } from './routes/you/appearance.js'
import { YouIndex, YouLayout } from './routes/you/layout.js'
import { MachinesSection } from './routes/you/machines.js'
import { NotificationsSection } from './routes/you/notifications.js'
import { ProvidersSection } from './routes/you/providers.js'
import { legacyTarget } from './routes/you/sections.js'
import { SecuritySection } from './routes/you/security.js'
import { NotFound } from './routes/you.js'

// ---- end ia-shell ----

const PUBLIC = new Set(['/signin', '/setup'])

function Splash() {
  return (
    <div
      role="status"
      className="grid h-dvh place-items-center"
      aria-busy="true"
      aria-label="Loading Viko"
    >
      <VMark size={44} />
    </div>
  )
}

/** Auth gate: session → setup complete → device trusted → app. */
function Root() {
  const b = useBackend()
  const path = useRouterState({ select: (s) => s.location.pathname })
  const session = useSession()
  const device = useQuery({
    queryKey: ['device'],
    queryFn: async () => {
      const d = await loadDevice()
      if (d) b.deviceKeyId = d.deviceKeyId
      const state = endorsementState(d)
      // A live "Just this session" endorsement opens the app; arm its pagehide revocation.
      if (state === 'session' && d) b.armSessionOnly(d.deviceKeyId)
      return { state }
    },
  })
  if (session.isPending || device.isPending) return <Splash />
  const s = session.data
  const authed = !!s?.authenticated
  if (!authed && !PUBLIC.has(path)) return <Navigate to="/signin" replace />
  if (authed && !s?.email && path !== '/setup') return <Navigate to="/setup" replace />
  if (authed && s?.setupState && s.setupState !== 'complete' && path !== '/setup')
    return <Navigate to="/setup" replace />
  if (authed && s?.setupState === 'complete') {
    const st = device.data?.state
    if (st && !endorsed(st) && path !== '/trust' && path !== '/setup')
      return <Navigate to="/trust" replace />
    if (path === '/signin') return <Navigate to={readPending()?.e ? '/enroll' : '/now'} replace />
  }
  return <Outlet />
}

export interface RouterContext {
  queryClient: QueryClient
  backend: Backend
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: NotFound,
})

const signin = createRoute({ getParentRoute: () => rootRoute, path: '/signin', component: SignIn })
const setup = createRoute({ getParentRoute: () => rootRoute, path: '/setup', component: Setup })
const trust = createRoute({
  getParentRoute: () => rootRoute,
  path: '/trust',
  component: TrustDevice,
})

/**
 * The signed-in app. The session (and device trust) is resolved *before* AppShell mounts, so no
 * authed call (`/api/runs`, `/ws/client`, …) ever leaves the browser without a session.
 */
const app = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppShell,
  beforeLoad: async ({ context }) => {
    const s = await context.queryClient
      .fetchQuery(sessionQuery(context.backend))
      .catch(() => undefined)
    if (!s?.authenticated) throw redirect({ to: '/signin', replace: true })
    if (!s.email) throw redirect({ to: '/setup', replace: true })
    if (s.setupState && s.setupState !== 'complete') throw redirect({ to: '/setup', replace: true })
    const dev = await loadDevice()
    if (dev) context.backend.deviceKeyId = dev.deviceKeyId
    const st = endorsementState(dev)
    // 'session' (a live "Just this session" key) is accepted like a trusted device; an expired one
    // reads as 'unendorsed' and goes back to /trust (the hub has ended its sign-in by then).
    if (!endorsed(st)) throw redirect({ to: '/trust', replace: true })
    if (st === 'session' && dev) context.backend.armSessionOnly(dev.deviceKeyId)
  },
})
const index = createRoute({
  getParentRoute: () => app,
  path: '/',
  beforeLoad: () => {
    // With the tab shell on, `/` opens the last project tab (Home when there are none).
    throw redirect({ to: isEnabled('lanesTab') ? '/p' : '/now' })
  },
})
const now = createRoute({
  getParentRoute: () => app,
  path: '/now',
  component: legacyPage(NowPage), // ia-shell
  // ---- ui-p0: `?i=decision:<id>` opens the decision inspector in place (Now's Decide) ----
  validateSearch: (s: Record<string, unknown>): { i?: string; intent?: 'reply' | 'delegate' } => ({
    ...(typeof s.i === 'string' && s.i.length <= 300 ? { i: s.i } : {}),
    ...(s.intent === 'reply' || s.intent === 'delegate' ? { intent: s.intent } : {}),
  }),
  // ---- end ui-p0 ----
})
const nowRun = createRoute({ getParentRoute: () => now, path: '$runId', component: () => null })
// ---- now-inbox: `?i=decision:<id>` / `?i=ask:<hub id>` opens the inspector (push deep links) ----
const inbox = createRoute({
  getParentRoute: () => app,
  path: '/inbox',
  component: legacyPage(InboxPage), // ia-shell
  validateSearch: (s: Record<string, unknown>): { i?: string; intent?: 'reply' | 'delegate' } => ({
    ...(typeof s.i === 'string' && s.i.length <= 300 ? { i: s.i } : {}),
    ...(s.intent === 'reply' || s.intent === 'delegate' ? { intent: s.intent } : {}),
  }),
})
// ---- end now-inbox ----
// ---- g12-overview: all-projects at-a-glance ----
const overview = createRoute({
  getParentRoute: () => app,
  path: '/overview',
  component: legacyPage(OverviewPage), // ia-shell
})
// ---- end g12-overview ----
const inboxRun = createRoute({ getParentRoute: () => inbox, path: '$runId', component: () => null })
const projects = createRoute({
  getParentRoute: () => app,
  path: '/projects',
  component: legacyPage(ProjectsPage), // ia-shell
})
const TABS = new Set([
  'board',
  'history',
  'landing',
  'artifacts',
  'settings',
  'feedback',
  'changes',
  'research', // g8-voice
  'import', // ingest-sync
]) // g9-feedback, g13-digests
const project = createRoute({
  getParentRoute: () => projects,
  path: '$projectId',
  component: () => null,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'board' | 'landing' | 'artifacts' | 'settings' | 'feedback'
    kind?: string
    since?: number
  } => ({
    ...(typeof s.tab === 'string' && TABS.has(s.tab) ? { tab: s.tab as 'board' } : {}),
    // Changes tab filters (the since-line counts drill to these).
    ...(typeof s.kind === 'string' && /^[a-z-]{1,20}$/.test(s.kind) ? { kind: s.kind } : {}),
    ...(typeof s.since === 'number' && Number.isFinite(s.since) ? { since: s.since } : {}),
  }),
})
const workstream = createRoute({
  getParentRoute: () => app,
  path: '/w/$workstreamId',
  component: legacyPage(WorkstreamPage), // ia-shell
})
const task = createRoute({
  getParentRoute: () => app,
  path: '/t/$taskId',
  component: legacyPage(TaskPage), // ia-shell
})
const rules = createRoute({
  getParentRoute: () => app,
  path: '/rules',
  component: legacyPage(() => <RulesPage />), // ia-shell
})
const releases = createRoute({
  getParentRoute: () => app,
  path: '/releases',
  component: legacyPage(() => <ReleasesPage />), // ia-shell
})
const you = createRoute({
  getParentRoute: () => app,
  path: '/you',
  component: legacyPage(YouLayout), // ia-shell
  validateSearch: (
    s: Record<string, unknown>,
  ): { grantMachines?: string; grantProjects?: string } => ({
    ...(typeof s.grantMachines === 'string' ? { grantMachines: s.grantMachines } : {}),
    ...(typeof s.grantProjects === 'string' ? { grantProjects: s.grantProjects } : {}),
  }),
})
/** Old `/you#machines` style anchors redirect to the section that now holds them. */
const youIndex = createRoute({
  getParentRoute: () => you,
  path: '/',
  component: YouIndex,
  beforeLoad: ({ location }) => {
    if (isEnabled('lanesTab')) return // ia-shell: the layout redirects to /settings
    const target = legacyTarget(location.hash)
    if (target)
      throw redirect({
        to: `/you/${target}` as never,
        hash: location.hash.replace(/^#/, ''),
        search: location.search as never,
        replace: true,
      })
  },
})
const youSection = (id: string, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => you, path: id, component })
const youChildren = [
  youIndex,
  youSection('account', AccountSection),
  youSection('security', SecuritySection),
  youSection('machines', MachinesSection),
  youSection('providers', ProvidersSection),
  youSection('notifications', NotificationsSection),
  youSection('appearance', AppearanceSection),
  youSection('advanced', AdvancedSection),
  youSection('research', ResearchSection), // g8-voice
]
const enroll = createRoute({
  getParentRoute: () => app,
  path: '/you/enroll',
  component: legacyPage(EnrollPage), // ia-shell
})
/** `viko setup` / `viko enroll` links land here (the fragment was captured at boot). */
const enrollLink = createRoute({
  getParentRoute: () => app,
  path: '/enroll',
  component: EnrollLinkPage,
})
/** Invite links are rewritten to /setup at boot; this catches client-side navigations. */
const inviteLink = createRoute({
  getParentRoute: () => rootRoute,
  path: '/i/$code',
  beforeLoad: () => {
    throw redirect({ to: '/setup', replace: true })
  },
})
const capture = createRoute({ getParentRoute: () => app, path: '/capture', component: CapturePage })
// Phone: the Explorer tree as a page ("Projects and lanes", from the top bar's switcher).
const tree = createRoute({ getParentRoute: () => app, path: '/tree', component: ExplorerPage })
// ---- share-to-viko: Web Share Target / Shortcuts handoff screen ----
const share = createRoute({ getParentRoute: () => app, path: '/share', component: SharePage })
// ---- end share-to-viko ----
// ---- g8-voice: hands-free voice mode ----
const voice = createRoute({ getParentRoute: () => app, path: '/voice', component: VoicePage })
// ---- end g8-voice ----
const session = createRoute({
  getParentRoute: () => app,
  path: '/s/$runId',
  // ia-shell: the short run link resolves the run's project (routes/legacy.tsx).
  beforeLoad: ({ params }) => {
    if (!isEnabled('lanesTab')) throw redirect({ to: '/now/$runId', params })
  },
  component: LegacyRedirect,
})

// ---- lanes-shell: project tabs (`/p/$projectId`, `?i=` inspector) and the mock-only gallery ----
const projectTabs = createRoute({
  getParentRoute: () => app,
  path: '/p',
  component: ProjectTabIndex,
})
const projectTabLayout = createRoute({
  getParentRoute: () => app,
  path: '/p/$projectId',
  component: ProjectTabPage,
  validateSearch: (s: Record<string, unknown>): { i?: string } => ({
    ...(typeof s.i === 'string' && s.i.length <= 300 ? { i: s.i } : {}),
  }),
})
// ---- ia-shell: the project's views and its runs, inside the tab's chrome ----
const projectView = (path: string, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => projectTabLayout, path, component })
const projectTab = projectTabLayout.addChildren([
  projectView('/', LanesTab),
  projectView('goal', GoalTab),
  projectView('history', ProjectHistoryView),
  projectView('releases', ProjectBuildsView),
  projectView('artifacts', ProjectArtifactsView),
  projectView('settings', ProjectSettingsView),
  // A workstream / task popped out of the inspector, full width inside the tab.
  projectView('w/$workstreamId', WorkstreamPage),
  projectView('t/$taskId', TaskPage),
  createRoute({
    getParentRoute: () => projectTabLayout,
    path: 's/$runId',
    component: ProjectRunPage,
    // `?seq=N` scrolls to an event; `?at=decision:<id>` to the ask behind a decision.
    validateSearch: (s: Record<string, unknown>): { seq?: number; at?: string } => {
      const seq = Number(s.seq)
      return {
        ...(Number.isSafeInteger(seq) && seq > 0 ? { seq } : {}),
        ...(typeof s.at === 'string' && s.at.length <= 300 ? { at: s.at } : {}),
      }
    },
  }),
])
// ---- end ia-shell ----
// Mock builds only: the artboard scenarios gallery (the branch is dropped from production bundles).
const lanesRoutes =
  import.meta.env.VITE_VIKO_MOCK === '1'
    ? [
        projectTabs,
        projectTab,
        createRoute({
          getParentRoute: () => app,
          path: '/dev/lanes',
          component: LanesGallery,
          validateSearch: (s: Record<string, unknown>): { state?: string; i?: string } => ({
            ...(typeof s.state === 'string' ? { state: s.state } : {}),
            ...(typeof s.i === 'string' ? { i: s.i } : {}),
          }),
        }),
      ]
    : [projectTabs, projectTab]
// ---- end lanes-shell ----

// ---- ia-shell: Home and Settings ----
const home = createRoute({
  getParentRoute: () => app,
  path: '/home',
  component: HomePage,
  validateSearch: validateHomeSearch,
})
const settings = createRoute({
  getParentRoute: () => app,
  path: '/settings',
  component: SettingsLayout,
  validateSearch: (
    s: Record<string, unknown>,
  ): { grantMachines?: string; grantProjects?: string } => ({
    ...(typeof s.grantMachines === 'string' ? { grantMachines: s.grantMachines } : {}),
    ...(typeof s.grantProjects === 'string' ? { grantProjects: s.grantProjects } : {}),
  }),
})
const settingsSection = (id: string, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => settings, path: id, component })
const settingsChildren = [
  createRoute({ getParentRoute: () => settings, path: '/', component: SettingsIndex }),
  settingsSection('machines', MachinesSection),
  settingsSection('providers', ProvidersSection),
  settingsSection('rules', RulesSection),
  settingsSection('research', ResearchSection),
  settingsSection('notifications', NotificationsSection),
  settingsSection('appearance', AppearanceKeysSection),
  settingsSection('security', SecuritySection),
  settingsSection('account', AccountSection),
  settingsSection('advanced', AdvancedSection),
  settingsSection('enroll', EnrollPage),
]
// ---- end ia-shell ----

export const routeTree = rootRoute.addChildren([
  signin,
  setup,
  trust,
  inviteLink,
  app.addChildren([
    index,
    now.addChildren([nowRun]),
    inbox.addChildren([inboxRun]),
    projects.addChildren([project]),
    workstream,
    task,
    rules,
    releases,
    you.addChildren(youChildren),
    enroll,
    enrollLink,
    capture,
    tree,
    share, // share-to-viko
    voice, // g8-voice
    session,
    overview, // g12-overview
    home, // ia-shell
    settings.addChildren(settingsChildren), // ia-shell
    ...lanesRoutes,
  ]),
])

export function makeRouter(context: RouterContext) {
  return createRouter({ routeTree, context, defaultPreload: 'intent', scrollRestoration: false })
}

/** A 401 from any authed call means the session ended: forget it and let the gate route. */
export function onUnauthorized(queryClient: QueryClient, backend: Backend) {
  backend.clearSessionCache?.()
  backend.hub.disconnect()
  queryClient.setQueryData(qk.session, { authenticated: false })
}
